/*
 * Copyright (c) 2020. American Express Company
 */

import Drawer from '@mui/material/Drawer/Drawer';
import * as _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';

import 'src/components/login-form/LoginForm.scss';
import { CardModel } from 'src/types/card.model';

import BwTooltipService from '../../services/bw-tooltip.service';
import CardService from '../../services/card.service';
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';
import CashFlowService from '../../services/cashflow.service';
import AccountDrawer from './AccountDrawer';
import AddAccountsDrawer from './add-accounts-drawer/AddAccountsDrawer';
import CryptoDrawer from './CryptoDrawer';
import AgeDrawer from './AgeDrawer';
import BuyHomeDrawer from '../../components/goal-drawer/BuyHomeDrawer';
import CharityDrawer from '../../components/goal-drawer/CharityDrawer';
import CollectSocialSecurityDrawer from '../../components/goal-drawer/CollectSocialSecurityDrawer';
import GenericSavingsDrawer from './common/GenericSavingsDrawer';
import DateGoalDrawer from '../../components/goal-drawer/DateGoalDrawer';
import DebtDrawer from 'src/components/debt-drawer/DebtDrawer';
import Disclosures from './disclosures/Disclosures';
import EmergencyFundDrawer from '../../components/goal-drawer/EmergencyFundDrawer';
import ExpenseDrawer from './ExpenseDrawer';
import ExpenseGoalDrawer from './ExpenseGoalDrawer';
import ExpensesDrawer from './ExpensesDrawer';
import FinancialProfileDrawer from './financial-profile-drawer/FinancialProfileDrawer';
import GoalsDrawer from './GoalsDrawer';
import IncomeDrawer from './IncomeDrawer';
import IncomesDrawer from './IncomesDrawer';
import InvestmentDrawer from '../../components/investment-drawer/InvestmentDrawer';
import JobDeltaDrawer from '../../components/goal-drawer/JobDeltaDrawer';
import LargePurchaseDrawer from '../../components/goal-drawer/LargePurchaseDrawer';
import LifeInsuranceDrawer from '../../components/goal-drawer/LifeInsuranceDrawer';
import LoseJobDrawer from '../../components/goal-drawer/LoseJobDrawer';
import MatchDrawer from './MatchDrawer';
import MonthlyExpenseDrawer from '../../components/goal-drawer/MonthlyExpenseDrawer';
import NewJobDrawer from '../../components/goal-drawer/NewJobDrawer';
import OnboardingDrawer, { PlaidContext } from '../../components/atomic/pages/onboarding/OnboardingDrawer';
import PayDownDebtDrawer from '../../components/goal-drawer/PayDownDebtDrawer';
import PlanForEducationCostsDrawer from '../../components/goal-drawer/PlanForEducationCostsDrawer';
import RealEstateDrawer from './RealEstateDrawer';
import AdjustExpensesDrawer from '../../components/goal-drawer/AdjustExpensesDrawer';
import IncomeExpenseDrawer from '../../components/report-drawers/IncomeExpenseDrawer';
import AssetDebtDrawer from '../../components/report-drawers/AssetDebtDrawer';
import NetWorthDrawer from '../../components/report-drawers/NetWorthDrawer';
import StatusDrawerAlternate from './status-drawer/StatusDrawerAlternate';
import TakeVacationDrawer from '../../components/goal-drawer/TakeVacationDrawer';
import TimelineDrawer from './timeline-drawer/TimelineDrawer';
import UnifiedReviewTransactionsDrawer from '../../components/atomic/organisms/unified-transactions/UnifiedReviewTransactionsDrawer';
import TransactionsView from '../../components/atomic/organisms/unified-transactions/TransactionsView';
import { TransactionsTableContext } from 'src/components/atomic/organisms/unified-transactions/UnifiedTransactionsTable';
import { TransactionsViewViewModel } from 'src/types/view-models/transactions/transactionsView.view.model';
import UnifiedRulesDrawer from '../../components/atomic/organisms/unified-transactions/UnifiedRulesDrawer';
import WindfallDrawer from '../../components/goal-drawer/WindfallDrawer';
import ZeroExpensesDrawer from './ZeroExpensesDrawer';
import ZeroIncomeDrawer from './ZeroIncomeDrawer';
import GrowFamilyDrawer from '../../components/goal-drawer/GrowFamilyDrawer';
import InsightsView from 'src/components/atomic/pages/insights/InsightsView';
import UtilsService from 'src/services/utils.service';
import { DebtDrawerViewModel } from 'src/types/view-models/debt-drawer.view.model';
import { InvestmentDrawerViewModel } from 'src/types/view-models/investment-drawer.view.model';
import { IncomeDrawerViewModel } from 'src/types/view-models/income-drawer.view.model';
import { StartupSteps } from 'src/types/onboarding.enum';
import BudgetHistoryView from 'src/components/atomic/pages/budgets/history/BudgetHistoryView';

import CashAndBankingAddAccountView from 'src/components/atomic/pages/addAccounts/CashAndBankingAddAccountView';
import InvestmentsAndRetirementAddAccountView from 'src/components/atomic/pages/addAccounts/InvestmentsAndRetirementAddAccountView';
import PropertyAndAssetsAddAccountView from 'src/components/atomic/pages/addAccounts/PropertyAndAssetsAddAccountView';
import LoansAndDebtAddAccountView from 'src/components/atomic/pages/addAccounts/LoansAndDebtAddAccountView';
import BudgetPersonalizeView from 'src/components/atomic/pages/budgets/personalize/BudgetPersonalizeView';
import BudgetIndexView from 'src/components/atomic/pages/budgets/index/BudgetIndexView';
import GlobalHeader from 'src/components/navigation/GlobalHeader';

export default class BwDrawer extends Component<any> {
    public override componentDidUpdate(prevProps: any, prevState: any, snapshot: any): void {
        BwTooltipService.setTooltips('.bw-drawer');
    }

    public override render(): ReactComponentElement<any> {
        let drawer: JSX.Element;
        let footer: JSX.Element;
        let drawerClass: string = 'bw-main-drawer';
        let useGenericDrawer: boolean = false;

        const props: any = {
            ageCursor: this.props.ageCursor,
            context: this.props.context,
            currentNetWorth: this.props.currentNetWorth,
            dismissMentors: this.props.dismissMentors,
            drawerProps: { ...this.props.drawerProps, history: this.props.history },
            filterSummaries: this.props.filterSummaries,
            firstImport: this.props.firstImport,
            getCards: this.props.getCards,
            getIncome: this.props.getIncome,
            goToCard: this.props.goToCard,
            handleBack: this.props.handleBack,
            handleAccounts: this.props.handleAccounts,
            handleAddDebt: this.props.handleAddDebt,
            handleAddCrypto: this.props.handleAddCrypto,
            handleAddExpense: this.props.handleAddExpense,
            handleAddIncome: this.props.handleAddIncome,
            handleAddIncomeRule: this.props.handleAddIncomeRule,
            handleAddInvestment: this.props.handleAddInvestment,
            handleCancelIncome: this.props.handleCancelIncome,
            handleChangeCard: this.props.handleChangeCard,
            handleChangeCardAttribute: this.props.handleChangeCardAttribute,
            handleChangeCardAttributes: this.props.handleChangeCardAttributes,
            handleChangeCardDate: this.props.handleChangeCardDate,
            handleChangeCardValue: this.props.handleChangeCardValue,
            handleChangeUser: this.props.handleChangeUser,
            handleClearFirstImport: this.props.handleClearFirstImport,
            handleClearUserAttribute: this.props.handleClearUserAttribute,
            handleClickOpenTransactions: this.props.handleClickOpenTransactions,
            handleCreateCard: this.props.handleCreateCard,
            handleCreateLinkedSavingsCard: this.props.handleCreateLinkedSavingsCard,
            handleDeleteCard: this.props.handleDeleteCard,
            handleDeleteIncome: this.props.handleDeleteIncome,
            handleDeleteIncomeRule: this.props.handleDeleteIncomeRule,
            handleDeleteLinkedSavingsCard: this.props.handleDeleteLinkedSavingsCard,
            handleDeleteRule: this.props.handleDeleteRule,
            handleDisplayIncome: this.props.handleDisplayIncome,
            handleEditAccount: this.props.handleEditAccount,
            handleEditDebt: this.props.handleEditDebt,
            handleEditIncome: this.props.handleEditIncome,
            handleEditInvestment: this.props.handleEditInvestment,
            handleEditRealEstate: this.props.handleEditRealEstate,
            handleFirstImport: this.props.handleFirstImport,
            handleIncomeChange: this.props.handleIncomeChange,
            handleIncomeDateChange: this.props.handleIncomeDateChange,
            handleOpenCard: this.props.handleOpenCard,
            handleOpenConfirmation: this.props.handleOpenConfirmation,
            handleOpenExpenses: this.props.handleOpenExpenses,
            handleOpenIncome: this.props.handleOpenIncome,
            handleOpenIncomeSummary: this.props.handleOpenIncomeSummary,
            handleOpenLinkedCard: this.props.handleOpenLinkedCard,
            handleOpenPayer: this.props.handleOpenPayer,
            handleOpenRecurringExpense: this.props.handleOpenRecurringExpense,
            handleOpenReviewExpenseTransactions: this.props.handleOpenReviewExpenseTransactions,
            handleOpenReviewIncomeTransactions: this.props.handleOpenReviewIncomeTransactions,
            handleOpenSignup: this.props.handleOpenSignup,
            handleOpenTransactions: this.props.handleOpenTransactions,
            handleOpenTransactionWithId: this.props.handleOpenTransactionWithId,
            handleReconnectInstitution: this.props.handleReconnectInstitution,
            handleReduceExpenses: this.props.handleReduceExpenses,
            handleReloadUser: this.props.handleReloadUser,
            handleSaveIncome: this.props.handleSaveIncome,
            handleShowWorkAssets: this.props.handleShowWorkAssets,
            handleShowWorkDebts: this.props.handleShowWorkDebts,
            handleShowWorkExpenses: this.props.handleShowWorkExpenses,
            handleShowWorkIncomes: this.props.handleShowWorkIncomes,
            handleShowWorkNetworth: this.props.handleShowWorkNetworth,
            handleTopDrawer: this.props.handleTopDrawer,
            handleUpdateCard: this.props.handleUpdateCard,
            handleUpdateIncomeRule: this.props.handleUpdateIncomeRule,
            handleUpdateRule: this.props.handleUpdateRule,
            handleUpdateUserAttribute: this.props.handleUpdateUserAttribute,
            handleUpdateUserAttributes: this.props.handleUpdateUserAttributes,
            handleUpdateUser: this.props.handleUpdateUser,
            handleView: this.props.handleView,
            handleZoom: this.props.handleZoom,
            hasData: this.props.hasData,
            hasDebts: this.props.hasDebts,
            hasExpenses: this.props.hasExpenses,
            hasIncome: this.props.hasIncome,
            hasInvestments: this.props.hasInvestments,
            hideLoader: this.props.hideLoader,
            hideNextStep: this.props.hideNextStep,
            history: this.props.history,
            incrementStartStep: this.props.incrementStartStep,
            markAchievementDone: this.props.markAchievementDone,
            markExpenseTransactionsReviewed: this.props.markExpenseTransactionsReviewed,
            markIncomeTransactionsReviewed: this.props.markIncomeTransactionsReviewed,
            mentor: this.props.mentor,
            mentorDisplayed: this.props.mentorDisplayed,
            monthsPerPeriod: this.props.monthsPerPeriod,
            netWorthDelta: this.props.netWorthDelta,
            onboardingRetireAge: this.props.onboardingRetireAge,
            processAction: this.props.processAction,
            setDrawer: this.props.setDrawer,
            setMentorState: this.props.setMentorState,
            setRetireAge: this.props.setRetireAge,
            setTransactions: this.props.setTransactions,
            updateFilteredTransactions: this.props.updateFilteredTransactions, // TODO: this should be removed once we move to the new Transactions View after refresh
            handleChangeIncomeSource: this.props.handleChangeIncomeSource, // TODO: this should be removed once we move to the new Transactions View after refresh
            handleDisplayTransaction: this.props.handleDisplayTransaction, // TODO: this should be removed once we move to the new Transactions View after refresh
            handleIgnoreFutureTransaction: this.props.handleIgnoreFutureTransaction, // TODO: this should be removed once we move to the new Transactions View after refresh
            handleSortUnifiedTransaction: this.props.handleSortUnifiedTransaction, // TODO: this should be removed once we move to the new Transactions View after refresh
            handleChangeTransactionGroup: this.props.handleChangeTransactionGroup, // TODO: this should be removed once we move to the new Transactions View after refresh
            recategorizeSelectedIncomes: this.props.recategorizeSelectedIncomes, // TODO: this should be removed once we move to the new Transactions View after refresh
            recategorizeSelectedExpenses: this.props.recategorizeSelectedExpenses, // TODO: this should be removed once we move to the new Transactions View after refresh
            changeFrequenceOfSelected: this.props.changeFrequenceOfSelected, // TODO: this should be removed once we move to the new Transactions View after refresh
            showLoader: this.props.showLoader,
            showNextStep: this.props.showNextStep,
            startupStep: this.props.user.attributes.startupStep,
            triggerGoalDrawer: this.props.triggerGoalDrawer,
            updateAfterIncomeRulesChange: this.props.updateAfterIncomeRulesChange,
            updateAfterRulesChange: this.props.updateAfterRulesChange,
            updateDrawer: this.props.updateDrawer,
            updateStartStep: this.props.updateStartStep,
            updateUserLocally: this.props.updateUserLocally,
            user: this.props.user,
            validIncomeSources: this.props.validIncomeSources,
            validIncomeSourcesToEnd: this.props.validIncomeSourcesToEnd,
            zoom: this.props.zoom,
            transactionFactory: this.props.transactionFactory
        };

        // TODO: Stop passing all props to all drawers--this is a state churn nightmare
        switch (this.props.drawer) {
            // eslint-disable-next-line default-case-last
            default:
            case 'start':
                if (this.props.user.attributes.startupStep || this.props.user.attributes.startupStep === 0) {
                    if (this.props.user.attributes.startupStep < StartupSteps.MEET_YOUR_PLAN) {
                        drawerClass = 'bw-full-drawer';
                    }
                    drawer = <OnboardingDrawer {...props} />;
                } else {
                    drawer = <StatusDrawerAlternate {...props} />;
                    footer = <Disclosures />;
                }
                break;
            case 'account':
                drawerClass = 'bw-full-drawer full-launch-drawer';
                drawer = (
                    <>
                        <GlobalHeader />
                        <AccountDrawer {...props} />
                    </>
                );
                break;
            case 'add-accounts':
                drawer = <AddAccountsDrawer handleBack={props.handleBack} history={props.history} />;
                footer = <Disclosures />;
                break;
            case 'add-accounts/cash-and-banking':
                drawer = (
                    <CashAndBankingAddAccountView
                        handleReloadUser={props.handleReloadUser}
                        handleBack={props.handleBack}
                        history={props.history}
                    />
                );
                useGenericDrawer = true;
                break;
            case 'add-accounts/investments-and-retirement':
                drawer = (
                    <InvestmentsAndRetirementAddAccountView
                        handleReloadUser={props.handleReloadUser}
                        handleBack={props.handleBack}
                        history={props.history}
                    />
                );
                useGenericDrawer = true;
                break;
            case 'add-accounts/property-and-assets':
                drawer = (
                    <PropertyAndAssetsAddAccountView
                        handleReloadUser={props.handleReloadUser}
                        handleBack={props.handleBack}
                        history={props.history}
                    />
                );
                useGenericDrawer = true;
                break;
            case 'add-accounts/loans-and-debt':
                drawer = (
                    <LoansAndDebtAddAccountView
                        handleReloadUser={props.handleReloadUser}
                        handleBack={props.handleBack}
                        history={props.history}
                    />
                );
                useGenericDrawer = true;
                break;
            case 'age':
                drawer = <AgeDrawer {...props} />;
                footer = <Disclosures />;
                break;
            case 'budget-adjust':
                drawerClass = 'bw-full-drawer';
                drawer = (
                    <>
                        <GlobalHeader />
                        <BudgetPersonalizeView history={props.drawerProps.history} />
                    </>
                );
                break;
            case 'budget-history':
                drawerClass = 'bw-full-drawer';
                drawer = (
                    <>
                        <GlobalHeader />
                        <BudgetHistoryView
                            propHandleOpenTransactions={props.handleOpenTransactions}
                            propHistory={props.drawerProps.history}
                            handleBack={props.handleBack}
                        />
                    </>
                );
                break;
            case 'buy-home-goal':
                drawer = <BuyHomeDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'collect-social-security-goal':
                drawer = <CollectSocialSecurityDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'charity-goal':
                drawer = <CharityDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'date-goal':
                drawer = <DateGoalDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'debt':
                drawer = (
                    <DebtDrawer
                        currentDebt={props.drawerProps.currentDebt}
                        lastCashFlowEndDate={FormatService.formatSqlDate(
                            CashFlowService.getLastCashFlow(this.props.user?.cashFlow)?.endDate
                        )}
                        onStartupStep={this.props.user?.attributes?.startupStep}
                        {...UtilsService.partiallyCLoneObject<any, DebtDrawerViewModel>(
                            props,
                            new DebtDrawerViewModel()
                        )}
                    />
                );
                footer = <Disclosures />;
                break;
            case 'emergency-fund':
                drawer = <EmergencyFundDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'expense-goal':
                if (this.props.drawerProps.card.description === 'have a baby') {
                    drawer = <GrowFamilyDrawer {...props} />;
                    useGenericDrawer = true;
                } else {
                    // not sure if this expense goal drawer is being used outside have a baby, but just in case
                    drawer = <ExpenseGoalDrawer {...props} />;
                    footer = <Disclosures />;
                }
                break;
            case 'expenses':
                drawer = <ExpensesDrawer hasFamily={props.user?.persons?.length > 1} {...props} />;
                footer = <Disclosures />;
                break;
            case 'extended-expense':
                drawer = <MonthlyExpenseDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'expense':
                drawer = <ExpenseDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'goals':
                drawer = <GoalsDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'income':
                drawer = (
                    <IncomeDrawer
                        {...UtilsService.partiallyCLoneObject<any, IncomeDrawerViewModel>(
                            props,
                            new IncomeDrawerViewModel()
                        )}
                    />
                );
                useGenericDrawer = true;
                break;
            case 'incomes':
                drawer = <IncomesDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'income-details':
                drawerClass = 'bw-full-drawer';
                drawer = (
                    <TransactionsTableContext.Provider
                        value={{
                            handleChangeIncomeSource: props.handleChangeIncomeSource,
                            handleDisplayTransaction: props.handleDisplayTransaction,
                            handleIgnoreFutureTransaction: props.handleIgnoreFutureTransaction,
                            handleSortUnifiedTransaction: props.handleSortUnifiedTransaction,
                            handleChangeTransactionGroup: props.handleChangeTransactionGroup,
                            recategorizeSelectedIncomes: props.recategorizeSelectedIncomes,
                            recategorizeSelectedExpenses: props.recategorizeSelectedExpenses,
                            changeFrequenceOfSelected: props.changeFrequenceOfSelected,
                            handleReloadUser: props.handleReloadUser,
                            handleDeleteIncomeRule: props.handleDeleteIncomeRule,
                            handleDeleteRule: props.handleDeleteRule
                        }}
                    >
                        <TransactionsView
                            {...UtilsService.partiallyCLoneObject<any, TransactionsViewViewModel>(
                                props.drawerProps,
                                new TransactionsViewViewModel()
                            )}
                            {...UtilsService.partiallyCLoneObject<any, TransactionsViewViewModel>(
                                props,
                                new TransactionsViewViewModel()
                            )}
                        />
                    </TransactionsTableContext.Provider>
                );
                break;
            case 'income-rules':
                drawerClass = 'bw-full-drawer';
                drawer = <UnifiedRulesDrawer {...props} />;
                break;
            case 'crypto':
                drawer = (
                    <CryptoDrawer
                        currentCrypto={props.drawerProps.currentInvestment}
                        cryptoList={props.drawerProps.crypto}
                        handleBack={props.handleBack}
                        handleAccounts={props.handleAccounts}
                        handleReloadUser={props.handleReloadUser}
                        history={props.history}
                    />
                );
                footer = <Disclosures />;
                break;
            case 'global-budget':
                drawerClass = 'bw-full-drawer';
                drawer = (
                    <>
                        <GlobalHeader />
                        <BudgetIndexView propHistory={props.history} handleBack={props.handle} />
                    </>
                );
                break;
            case 'investment':
                drawer = (
                    <InvestmentDrawer
                        currentInvestment={props.drawerProps.currentInvestment}
                        typeFromURL={props.drawerProps.type}
                        onStartupStep={props.user?.attributes?.startupStep}
                        realEstateCount={props.user?.realEstate?.length}
                        {...UtilsService.partiallyCLoneObject<any, InvestmentDrawerViewModel>(
                            props,
                            new InvestmentDrawerViewModel()
                        )}
                    />
                );
                break;
            case 'job-delta-goal':
                drawer = <JobDeltaDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'new-job-goal':
                drawer = <NewJobDrawer {...props} />;
                footer = <Disclosures />;
                break;
            case 'large-purchase':
                drawer = <LargePurchaseDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'life-insurance-goal':
                drawer = <LifeInsuranceDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'retire':
            case 'lose-job-goal':
                drawer = <LoseJobDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'match':
                drawer = <MatchDrawer {...props} />;
                footer = <Disclosures />;
                break;
            case 'pay-down-debt':
                drawer = <PayDownDebtDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'insight':
                drawerClass = 'bw-full-drawer  full-launch-drawer';
                drawer = (
                    <>
                        <GlobalHeader />
                        <InsightsView
                            user={props.user}
                            stats={props.drawerProps.stats}
                            budgets={props.drawerProps.budgets}
                            summary={props.drawerProps.summary}
                            savingsGoalsCards={this.props.drawerProps.savingsGoalsCards}
                            history={props.drawerProps.history}
                            handleBack={props.handleBack}
                            handleUpdateUserAttribute={props.handleUpdateUserAttribute}
                            handleOpenIncomeSummary={props.handleOpenIncomeSummary}
                            handleOpenExpenses={props.handleOpenExpenses}
                            handleReviewExpenses={props.handleOpenReviewExpenseTransactions}
                            handleOpenTransactions={props.handleOpenTransactions}
                            handleOpenTransactionWithId={props.handleOpenTransactionWithId}
                            handleOpenCard={props.handleOpenCard}
                            handleOpenLinkedCard={props.handleOpenLinkedCard}
                        />
                    </>
                );
                break;
            case 'plan-for-education-costs':
                drawer = <PlanForEducationCostsDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'profile':
                drawerClass = 'bw-full-drawer full-launch-drawer';
                drawer = (
                    <>
                        <GlobalHeader />
                        <FinancialProfileDrawer {...props} />
                    </>
                );
                break;
            case 'report-income-expense':
                drawer = <IncomeExpenseDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'report-asset-debt':
                drawer = <AssetDebtDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'savings-goal':
                drawer = (
                    <GenericSavingsDrawer
                        {...props}
                        costText={LiteralsService.getLiteral('SavingsDrawer.savings.costText', [
                            FormatService.formatDateLongMonth(this.props.drawerProps.card.endDate)
                        ])}
                    />
                );
                footer = <Disclosures />;
                break;
            case 'save-for-family':
                drawer = (
                    <GenericSavingsDrawer
                        {...props}
                        costText={LiteralsService.getLiteral('SavingsDrawer.family.costText', [
                            FormatService.formatDateLongMonth(this.props.drawerProps.card.endDate)
                        ])}
                    />
                );
                footer = <Disclosures />;
                break;
            case 'save-for-large-purchase':
                drawer = (
                    <GenericSavingsDrawer
                        {...props}
                        costText={LiteralsService.getLiteral('SavingsDrawer.large.costText', [
                            FormatService.formatDateLongMonth(this.props.drawerProps.card.endDate)
                        ])}
                    />
                );
                footer = <Disclosures />;
                break;

            case 'save-for-education-costs':
                const linkedCardId: number = this.props.drawerProps.card.attributes.linked_card_id;
                let costText: string = LiteralsService.getLiteral('SavingsDrawer.education.costText');

                if (linkedCardId) {
                    const linkedCard = _.find(this.props.user.cards, { id: linkedCardId });
                    if (linkedCard) {
                        costText = LiteralsService.getLiteral('SavingsDrawer.education.costTextLinked', [
                            CardService.getPeriod(linkedCard)
                        ]);
                    }
                }
                drawer = <GenericSavingsDrawer {...props} costText={costText} />;
                footer = <Disclosures />;
                break;
            case 'save-for-home-goal':
                const card: CardModel = _.get(this.props, 'drawerProps.card');
                const buyGoal: CardModel = _.find(this.props.user.cards, {
                    id: card.attributes.linked_card_id
                });
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const down_payment: number = _.get(buyGoal, 'attributes.down_payment');
                drawer = (
                    <GenericSavingsDrawer
                        {...props}
                        costText={LiteralsService.getLiteral('SavingsDrawer.home.costText', [
                            FormatService.format(down_payment),
                            FormatService.formatDateMonth(card.endDate)
                        ])}
                    />
                );
                footer = <Disclosures />;
                break;
            case 'save-for-vacation-goal':
                drawer = (
                    <GenericSavingsDrawer
                        {...props}
                        costText={LiteralsService.getLiteral('SavingsDrawer.vacation.costText', [
                            FormatService.formatDateLongMonth(this.props.drawerProps.card.endDate)
                        ])}
                    />
                );
                footer = <Disclosures />;
                break;
            case 'save-for-retirement-goal':
                drawer = (
                    <GenericSavingsDrawer
                        {...props}
                        costText={LiteralsService.getLiteral('SavingsDrawer.retirement.costText', [
                            FormatService.formatDateLongMonth(this.props.drawerProps.card.endDate)
                        ])}
                    />
                );
                footer = <Disclosures />;
                break;
            case 'show-work-networth':
                drawer = <NetWorthDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'take-vacation':
                drawer = <TakeVacationDrawer {...props} />;
                footer = <Disclosures />;
                break;
            case 'timeline':
                drawer = <TimelineDrawer {...props} />;
                footer = <Disclosures />;
                break;
            case 'unified-transactions':
                drawerClass = 'bw-full-drawer';
                drawer = (
                    <TransactionsTableContext.Provider
                        value={{
                            handleChangeIncomeSource: props.handleChangeIncomeSource,
                            handleDisplayTransaction: props.handleDisplayTransaction,
                            handleIgnoreFutureTransaction: props.handleIgnoreFutureTransaction,
                            handleSortUnifiedTransaction: props.handleSortUnifiedTransaction,
                            handleChangeTransactionGroup: props.handleChangeTransactionGroup,
                            recategorizeSelectedIncomes: props.recategorizeSelectedIncomes,
                            recategorizeSelectedExpenses: props.recategorizeSelectedExpenses,
                            changeFrequenceOfSelected: props.changeFrequenceOfSelected,
                            handleReloadUser: props.handleReloadUser,
                            handleDeleteIncomeRule: props.handleDeleteIncomeRule,
                            handleDeleteRule: props.handleDeleteRule
                        }}
                    >
                        <GlobalHeader />
                        <TransactionsView
                            {...UtilsService.partiallyCLoneObject<any, TransactionsViewViewModel>(
                                props.drawerProps,
                                new TransactionsViewViewModel()
                            )}
                            {...UtilsService.partiallyCLoneObject<any, TransactionsViewViewModel>(
                                props,
                                new TransactionsViewViewModel()
                            )}
                        />
                    </TransactionsTableContext.Provider>
                );
                break;
            case 'transactions':
                drawerClass = 'bw-full-drawer';
                drawer = (
                    <TransactionsTableContext.Provider
                        value={{
                            handleChangeIncomeSource: props.handleChangeIncomeSource,
                            handleDisplayTransaction: props.handleDisplayTransaction,
                            handleIgnoreFutureTransaction: props.handleIgnoreFutureTransaction,
                            handleSortUnifiedTransaction: props.handleSortUnifiedTransaction,
                            handleChangeTransactionGroup: props.handleChangeTransactionGroup,
                            recategorizeSelectedIncomes: props.recategorizeSelectedIncomes,
                            recategorizeSelectedExpenses: props.recategorizeSelectedExpenses,
                            changeFrequenceOfSelected: props.changeFrequenceOfSelected,
                            handleReloadUser: props.handleReloadUser,
                            handleDeleteIncomeRule: props.handleDeleteIncomeRule,
                            handleDeleteRule: props.handleDeleteRule
                        }}
                    >
                        <TransactionsView
                            {...UtilsService.partiallyCLoneObject<any, TransactionsViewViewModel>(
                                props.drawerProps,
                                new TransactionsViewViewModel()
                            )}
                            {...UtilsService.partiallyCLoneObject<any, TransactionsViewViewModel>(
                                props,
                                new TransactionsViewViewModel()
                            )}
                        />
                    </TransactionsTableContext.Provider>
                );
                break;
            case 'real-estate':
                drawer = <RealEstateDrawer {...props} />;
                footer = <Disclosures />;
                break;
            case 'reduce-expenses-goal':
                drawer = <AdjustExpensesDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'review-transactions':
                drawerClass = 'bw-full-drawer';
                drawer = (
                    <>
                        <GlobalHeader />
                        <UnifiedReviewTransactionsDrawer {...props} />
                    </>
                );
                break;
            case 'rules':
                drawerClass = 'bw-full-drawer';
                drawer = (
                    <>
                        <GlobalHeader />
                        <UnifiedRulesDrawer {...props} />
                    </>
                );
                break;
            case 'windfall-goal':
                drawer = <WindfallDrawer {...props} />;
                useGenericDrawer = true;
                break;
            case 'zero-expenses':
                drawer = <ZeroExpensesDrawer {...props} />;
                footer = <Disclosures />;
                break;
            case 'zero-income':
                drawer = <ZeroIncomeDrawer {...props} />;
                footer = <Disclosures />;
                break;
        }

        drawer = (
            <PlaidContext.Provider
                value={{
                    handleClearFirstImport: props.handleClearFirstImport,
                    handleFirstImport: props.handleFirstImport,
                    handleReloadUser: props.handleReloadUser,
                    firstImport: props.firstImport,
                    user: props.user,
                    showLoader: props.showLoader,
                    hideLoader: props.hideLoader,
                    handleTopDrawer: props.handleTopDrawer,
                    handleAccounts: props.handleAccounts
                }}
            >
                {drawer}
            </PlaidContext.Provider>
        );

        // TODO: once all drawers are converted to GenericDrawer, we can remove the below and return in each case above
        if (useGenericDrawer) {
            return <>{drawer}</>;
        }

        return (
            <Drawer
                className={`${drawerClass} bw-drawer ${props.drawerProps.disabled ? 'disabled' : ''}`}
                variant="permanent"
                anchor="left"
                PaperProps={{
                    elevation: 18
                }}
            >
                {drawer}
                {footer}
            </Drawer>
        );
    }
}
